/**
 * Un almacén local por cuenta.
 *
 * La web (IndexedDB) y la app (SQLite) guardan sesiones, series, diario y la
 * cola de sincronización en el dispositivo. Cada cuenta tiene el suyo, y nada
 * de una cuenta puede verse ni subirse con otra. Aquí vive lo que comparten
 * las dos plataformas y se puede probar sin ellas:
 *
 * - {@link tokenSubject} y {@link bearerBelongsTo}: la última barrera antes de
 *   que una petición salga. Ninguna petición con el token de una cuenta sale
 *   mientras el dispositivo sirve los datos de otra.
 * - {@link claimLegacyStore}: el reparto del almacén compartido de antes, que
 *   no sabía de quién era cada fila.
 *
 * ## Regla del almacén antiguo
 *
 * Las filas antiguas no llevan `userId`. Su dueño se deduce así, en orden:
 *
 * 1. La **pista de sesión** que había al abrir la versión nueva por primera vez
 *    (la cuenta con sesión abierta en ese momento) se anota como dueña. Si la
 *    cuenta que entra es ésa, el almacén es suyo; si es otra, no lo es.
 * 2. Sin pista (se había cerrado sesión antes de actualizar), se pregunta al
 *    servidor, con la cuenta que entra, por las sesiones de entrenamiento del
 *    almacén. Si alguna existe para ella y ninguna de las que el dispositivo
 *    daba por subidas falta, es suyo. Si falta alguna que sí se subió, era de
 *    otra cuenta.
 * 3. En cualquier otro caso (sin pruebas, o sin red para preguntar) hay dudas:
 *    el almacén se deja apartado, sin subir nada, y se vuelve a evaluar en el
 *    siguiente inicio de sesión de cualquier cuenta.
 *
 * Cuando es suyo se mueve entero —cola incluida— al almacén de la cuenta, y
 * el antiguo se vacía y se borra. Un almacén antiguo vacío se borra sin más.
 */
// --- Token ------------------------------------------------------------------
const BASE64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
/** Decodifica base64url a texto UTF-8 sin depender de `atob` (no está en Hermes). */
function decodeBase64Url(input) {
    const clean = input.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    let bits = 0;
    let value = 0;
    let encoded = '';
    for (const char of clean) {
        const index = BASE64URL.indexOf(char);
        if (index === -1)
            return null;
        value = (value << 6) | index;
        bits += 6;
        if (bits >= 8) {
            bits -= 8;
            encoded += `%${((value >> bits) & 0xff).toString(16).padStart(2, '0')}`;
        }
    }
    try {
        return decodeURIComponent(encoded);
    }
    catch {
        return null;
    }
}
/**
 * Cuenta (`sub`) de un token de acceso JWT, o `null` si no se puede leer. No
 * valida la firma: sólo sirve para no mandar el token de una cuenta con los
 * datos de otra, y de la firma ya se ocupa el servidor.
 */
export function tokenSubject(token) {
    const parts = token.split('.');
    if (parts.length !== 3 || parts[1] === undefined)
        return null;
    const json = decodeBase64Url(parts[1]);
    if (json === null)
        return null;
    try {
        const payload = JSON.parse(json);
        return payload !== null && typeof payload.sub === 'string' && payload.sub !== ''
            ? payload.sub
            : null;
    }
    catch {
        return null;
    }
}
/**
 * ¿Puede salir una petición con esta cabecera `Authorization` mientras el
 * dispositivo sirve los datos de `account`?
 *
 * Sin token, siempre (login, registro, refresco, rutas públicas). Con token,
 * sólo si es de esa misma cuenta: sin cuenta abierta, o con el token de otra
 * (otra pestaña que ha cambiado de cuenta, una petición rezagada de la cuenta
 * anterior), no sale nada.
 */
export function bearerBelongsTo(authorization, account) {
    if (authorization === null || !/^Bearer\s+/i.test(authorization))
        return true;
    if (account === null)
        return false;
    return tokenSubject(authorization.replace(/^Bearer\s+/i, '').trim()) === account;
}
/** Aplica la regla del almacén antiguo (ver la cabecera del módulo). */
export function decideLegacyOwnership(input) {
    const { userId, recordedOwner, witnesses } = input;
    if (recordedOwner !== null)
        return recordedOwner === userId ? 'mine' : 'other';
    if (witnesses === null)
        return 'unknown';
    if (witnesses.missing > 0)
        return 'other';
    if (witnesses.unknown > 0)
        return 'unknown';
    return witnesses.found > 0 ? 'mine' : 'unknown';
}
/**
 * Anota quién era el dueño del almacén antiguo. Hay que llamarla al arrancar,
 * antes de que nada escriba o borre la pista de sesión, y sólo hace algo la
 * primera vez: lo anotado no se cambia después.
 */
export async function captureLegacyOwner(store, records, hintUserId) {
    if ((await records.read()) !== null)
        return;
    if (!(await store.exists()))
        return;
    await records.write({ owner: hintUserId });
}
/** Máximo de sesiones por las que se pregunta al servidor. */
const MAX_LOOKUPS = 6;
/**
 * Asigna el almacén antiguo al iniciar sesión `userId`. Hay que llamarla con
 * la cuenta ya identificada (para poder preguntar al servidor) y antes de
 * arrancar su cola de sincronización.
 */
export async function claimLegacyStore(options) {
    const { userId, store, records, lookupSession } = options;
    if (!(await store.exists())) {
        await records.clear();
        return 'none';
    }
    if (await store.isEmpty()) {
        await store.destroy();
        await records.clear();
        return 'none';
    }
    const record = await records.read();
    const recordedOwner = record?.owner ?? null;
    let witnesses = null;
    if (recordedOwner === null) {
        witnesses = await collectWitnesses(await store.sessions(), lookupSession);
    }
    const ownership = decideLegacyOwnership({ userId, recordedOwner, witnesses });
    if (ownership === 'other')
        return 'kept';
    if (ownership === 'unknown')
        return 'undecided';
    await store.moveInto(userId);
    await records.clear();
    await store.destroy();
    return 'claimed';
}
/**
 * Pregunta al servidor por unas pocas sesiones. Primero las que el dispositivo
 * daba por subidas, que son las únicas que pueden probar que el almacén es de
 * otra cuenta; si una de ellas falta, no hace falta seguir.
 */
async function collectWitnesses(sessions, lookupSession) {
    const ordered = [
        ...sessions.filter((session) => session.synced),
        ...sessions.filter((session) => !session.synced),
    ].slice(0, MAX_LOOKUPS);
    const witnesses = { found: 0, missing: 0, unknown: 0 };
    for (const session of ordered) {
        let result;
        try {
            result = await lookupSession(session.clientId);
        }
        catch {
            result = 'unknown';
        }
        if (result === 'found')
            witnesses.found += 1;
        else if (result === 'unknown')
            witnesses.unknown += 1;
        // Que no exista una sesión que nunca llegó a subirse no prueba nada.
        else if (session.synced) {
            witnesses.missing += 1;
            break;
        }
    }
    return witnesses;
}
//# sourceMappingURL=accounts.js.map